import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, ArrowUpRight, Sparkles, Users, TrendingUp, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { subHours } from "date-fns";

interface Row {
  source: string | null;
  hook: string | null;
  campaign: string | null;
  signup_timestamp: string | null;
  created_at: string;
}

const normalize = (s: string | null | undefined) => (s ? s.toLowerCase().trim() : "");

const topOf = (rows: Row[], key: keyof Row): { name: string; value: number } | null => {
  const map = new Map<string, number>();
  rows.forEach((r) => {
    const v = normalize(r[key] as string | null);
    if (!v) return;
    map.set(v, (map.get(v) || 0) + 1);
  });
  const sorted = Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  return sorted[0] ? { name: sorted[0][0], value: sorted[0][1] } : null;
};

const Stat = ({
  icon: Icon,
  label,
  value,
  sub,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string | number;
  sub?: string;
}) => (
  <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/40">
      <Icon className="w-3 h-3 text-cyan-400" />
      {label}
    </div>
    <div className="mt-1 text-xl font-semibold text-white truncate">{value}</div>
    {sub && <div className="text-[10px] text-white/40 truncate">{sub}</div>}
  </div>
);

const Insight = ({ children }: { children: React.ReactNode }) => (
  <div className="flex items-start gap-2 rounded-md border border-cyan-400/15 bg-cyan-400/[0.04] px-3 py-2 text-[12px] text-white/75">
    <Sparkles className="w-3.5 h-3.5 text-cyan-300 mt-0.5 shrink-0" />
    <span className="leading-snug">{children}</span>
  </div>
);

export default function AdminAnalyticsCard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<Row[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const { data, error: e } = await supabase
          .from("profiles")
          .select("source, hook, campaign, signup_timestamp, created_at")
          .order("signup_timestamp", { ascending: false, nullsFirst: false })
          .limit(2000);
        if (e) throw e;
        console.log("[admin-lab/analytics-card] loaded", data?.length);
        setRows((data as Row[]) || []);
      } catch (err: any) {
        console.error("[admin-lab/analytics-card] failed", err);
        setError(err?.message || "Failed");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const total = rows.length;
  const topSource = topOf(rows, "source");
  const topHook = topOf(rows, "hook");
  const topCampaign = topOf(rows, "campaign");
  const cutoff24 = subHours(new Date(), 24);
  const last24 = rows.filter((r) => {
    const ts = r.signup_timestamp || r.created_at;
    return ts && new Date(ts) >= cutoff24;
  }).length;

  // Lightweight comparative insights (best-effort placeholders rooted in real data)
  const sourceCounts = new Map<string, number>();
  rows.forEach((r) => {
    const v = normalize(r.source);
    if (v) sourceCounts.set(v, (sourceCounts.get(v) || 0) + 1);
  });
  const tiktok = sourceCounts.get("tiktok") || 0;
  const ig = sourceCounts.get("instagram") || 0;
  const yt = sourceCounts.get("youtube") || 0;

  const ratio = ig > 0 ? (tiktok / ig).toFixed(1) : tiktok > 0 ? "∞" : null;

  return (
    <div className="relative overflow-hidden rounded-2xl border border-cyan-400/20 bg-gradient-to-br from-[#0c1118] via-[#0a0d14] to-[#0a0d14] p-6">
      {/* Glow */}
      <div
        className="absolute -top-24 -right-24 w-64 h-64 rounded-full opacity-30 blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(34,211,238,0.5), transparent 70%)" }}
      />

      <div className="relative flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-cyan-300/80">
            <Activity className="w-3 h-3" />
            Behavioral Intelligence
          </div>
          <h2 className="mt-1 text-xl font-semibold text-white">
            Attribution Console
          </h2>
          <p className="mt-1 text-sm text-white/50 max-w-md">
            Where users discover GreyRock AI® and which emotional patterns are converting right now.
          </p>
        </div>
        <Link
          to="/admin/analytics"
          className="shrink-0 inline-flex items-center gap-1.5 rounded-md border border-cyan-400/40 bg-cyan-400/10 px-3 py-1.5 text-xs font-medium text-cyan-200 hover:bg-cyan-400/20 transition"
        >
          Open Analytics <ArrowUpRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-[72px] rounded-lg bg-white/[0.02] border border-white/5 animate-pulse" />
          ))}
        </div>
      ) : error ? (
        <div className="text-sm text-red-300/80">Couldn't load attribution data: {error}</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat icon={Users} label="Total Signups" value={total} />
            <Stat
              icon={TrendingUp}
              label="Top Source"
              value={topSource?.name || "—"}
              sub={topSource ? `${topSource.value} signups` : "no data"}
            />
            <Stat
              icon={Sparkles}
              label="Top Hook"
              value={topHook?.name || "—"}
              sub={topHook ? `${topHook.value} conversions` : "no data"}
            />
            <Stat icon={Clock} label="Last 24h" value={last24} sub="new signups" />
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2">
            {ratio && (
              <Insight>
                TikTok converting <span className="text-cyan-300 font-semibold">{ratio}x</span> higher than Instagram
              </Insight>
            )}
            {topHook && (
              <Insight>
                <span className="text-cyan-300 font-semibold">{topHook.name}</span> hook currently highest converting
              </Insight>
            )}
            {topCampaign && (
              <Insight>
                <span className="text-cyan-300 font-semibold">{topCampaign.name}</span> campaign rising in attribution
              </Insight>
            )}
            {yt > 0 && (
              <Insight>
                YouTube retention strongest among long-form sources
              </Insight>
            )}
            {!topSource && !topHook && !topCampaign && (
              <Insight>
                Awaiting attributed signups — tag links with <code className="text-cyan-300">?utm_source=tiktok&amp;utm_campaign=urgency&amp;hook=hot_cold</code>
              </Insight>
            )}
          </div>
        </>
      )}
    </div>
  );
}
